(function () {
  const STORAGE_KEY = 'viewedProducts';

  class BenchmarkCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.grid = this.querySelector('[data-cart-recommendations-grid]');
      this.limit = Number.parseInt(this.dataset.limit, 10) || 6;
      this.intent = this.dataset.intent || 'related';

      if (!this.grid) return;

      this.loadRecommendations();
    }

    async loadRecommendations() {
      const cartProductIds = await this.getCartProductIds();
      const sourceProductIds = this.unique([...cartProductIds, ...this.getRecentlyViewedProductIds()]).slice(0, 8);

      if (sourceProductIds.length === 0) return;

      this.grid.setAttribute('aria-busy', 'true');

      try {
        const recommendationGroups = await Promise.all(
          sourceProductIds.map((productId) => this.fetchRecommendations(productId))
        );
        const recommendations = this.mergeRecommendationGroups(recommendationGroups, cartProductIds);

        if (recommendations.length > 0) {
          this.grid.innerHTML = recommendations.map((product) => this.renderProduct(product)).join('');
        }
      } catch (error) {
        console.warn('Benchmark cart recommendations could not be loaded.', error);
      } finally {
        this.grid.removeAttribute('aria-busy');
      }
    }

    async getCartProductIds() {
      try {
        const response = await fetch(this.storefrontUrl('cart.js'), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return [];

        const cart = await response.json();
        return this.unique((cart.items || []).map((item) => item.product_id).filter(Boolean).map(String));
      } catch (error) {
        console.warn('Benchmark cart recommendations could not read the cart.', error);
        return [];
      }
    }

    getRecentlyViewedProductIds() {
      try {
        const viewedProducts = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

        if (!Array.isArray(viewedProducts)) return [];

        return viewedProducts.filter(Boolean).map(String);
      } catch (error) {
        return [];
      }
    }

    async fetchRecommendations(productId) {
      const url = this.storefrontUrl('recommendations/products.json');

      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', Math.min(Math.max(this.limit, 4), 10));
      url.searchParams.set('intent', this.intent);

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return [];

      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    }

    mergeRecommendationGroups(groups, cartProductIds) {
      const cartIds = new Set(cartProductIds.map(String));
      const seenIds = new Set();
      const mergedProducts = [];
      const largestGroupSize = Math.max(0, ...groups.map((group) => group.length));

      for (let productIndex = 0; productIndex < largestGroupSize; productIndex += 1) {
        for (const group of groups) {
          const product = group[productIndex];

          if (!product) continue;

          const productId = String(product.id);

          if (cartIds.has(productId) || seenIds.has(productId) || product.available === false) continue;

          seenIds.add(productId);
          mergedProducts.push(product);

          if (mergedProducts.length >= this.limit) {
            return mergedProducts;
          }
        }
      }

      return mergedProducts;
    }

    renderProduct(product) {
      const title = this.escapeHtml(product.title || '');
      const productUrl = this.productUrl(product);
      const imageUrl = this.imageUrl(product.featured_image);
      const media = imageUrl
        ? `<img src="${this.escapeAttribute(imageUrl)}" alt="${title}" loading="lazy">`
        : '<span class="benchmark-cart-recommendations__media-placeholder" aria-hidden="true"></span>';

      return `
        <div class="resource-list__item">
          <a class="benchmark-cart-recommendations__card" href="${this.escapeAttribute(productUrl)}">
            <span class="benchmark-cart-recommendations__media">${media}</span>
            <span class="benchmark-cart-recommendations__title">${title}</span>
            <span class="benchmark-cart-recommendations__price">${this.escapeHtml(this.formatMoney(product.price))}</span>
          </a>
        </div>
      `;
    }

    productUrl(product) {
      const fallbackPath = product.handle ? `/products/${product.handle}` : '#';
      const url = product.url || fallbackPath;

      try {
        const resolvedUrl = new URL(url, window.location.origin);

        if (resolvedUrl.origin === window.location.origin) {
          return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
        }

        return resolvedUrl.toString();
      } catch (error) {
        return fallbackPath;
      }
    }

    imageUrl(image) {
      if (!image) return '';

      if (image.startsWith('//')) {
        return `${window.location.protocol}${image}`;
      }

      return image;
    }

    storefrontUrl(path) {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;

      return new URL(`${normalizedRoot}${path}`, window.location.origin);
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'AUD';
      const locale = document.documentElement.lang || 'en-AU';

      try {
        return new Intl.NumberFormat(locale, {
          currency,
          style: 'currency',
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    unique(values) {
      return Array.from(new Set(values.map(String)));
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (character) => {
        const replacements = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        };

        return replacements[character];
      });
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  if (!customElements.get('benchmark-cart-recommendations')) {
    customElements.define('benchmark-cart-recommendations', BenchmarkCartRecommendations);
  }

  function upgradeLegacyCartProductList() {
    if (!window.location.pathname.endsWith('/cart')) return;
    if (document.querySelector('benchmark-cart-recommendations')) return;

    const legacySection = document.querySelector('.shopify-section.ui-test-product-list');
    const grid = legacySection?.querySelector('[data-testid="resource-list-grid"]');

    if (!legacySection || !grid) return;

    grid.setAttribute('data-cart-recommendations-grid', '');
    grid.classList.add('benchmark-cart-recommendations__grid');

    const wrapper = document.createElement('benchmark-cart-recommendations');
    const itemCount = grid.querySelectorAll('.resource-list__item').length;

    wrapper.className = 'benchmark-cart-recommendations';
    wrapper.dataset.limit = String(itemCount || 6);
    wrapper.dataset.intent = 'related';

    legacySection.parentNode.insertBefore(wrapper, legacySection);
    wrapper.appendChild(legacySection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', upgradeLegacyCartProductList, { once: true });
  } else {
    upgradeLegacyCartProductList();
  }
})();
